// Change a device's TYPE, where you found out it was wrong.
//
// The bench is the place this gets noticed: the control card beside it draws
// whatever `DeviceID` says, so a 4-gang drawn as a sensor — or a sensor that
// answers STATUS with four channels, which is how 1127089 was found — is
// visible the moment the number resolves. Fixing it here redraws the card
// without leaving the page.
//
// Two ways in, deliberately: a NAME from the catalogue, and a bare NUMBER. The
// install row stores the number, so an id the catalogue has not got (a type
// added on the server since the app cached it) must still be settable — that is
// the whole reason the number box exists beside the dropdown.
//
// ⚠️ A TYPE CHANGE IS NOT COSMETIC. `DeviceType.OTABinFileId` decides which
// firmware the device is SERVED, so changing the type can change what it
// flashes on its next Update Code — unless that device carries its own
// `DevicesInstalled.OTABinFileId`, which overrides the type's. The line under
// the control says which bin file each side points at, because a reflash with
// the wrong image reports success (the device stamps CodeUpdated before it
// flashes) and is expensive to undo.
//
// ⚠️ A WRONG DeviceID IS NEVER AN ERROR AT EITHER END. The device keeps sending
// exactly the same bytes; only the app's drawing changes. Nothing will tell you
// it is wrong except the controls looking wrong.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Autocomplete, TextField, Button, Alert, Typography,
} from '@mui/material';
import { setDeviceType } from '../../core/devices/deviceAdmin';
import { loadTypeChoices } from '../../core/admin/deviceTracking';
import { getDeviceTypes } from '../../core/api/api';

const idOf = (d) => (d && d.DeviceID != null ? Number(d.DeviceID) : null);

/**
 * @param device   the resolved install row (needs UniqueNumber + DeviceID)
 * @param onSaved  called after the write, so the caller can re-read the row
 * @param dense    phone layout: stack rather than sit in a line
 */
export default function DeviceTypePicker({ device, onSaved, dense = false }) {
  const [types, setTypes] = useState([]);
  const [rows, setRows] = useState([]);          // the raw catalogue, for bin files
  const [idText, setIdText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  useEffect(() => {
    loadTypeChoices().then(setTypes).catch(() => setTypes([]));
    getDeviceTypes()
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]));
  }, []);

  // A different device is a different answer: never carry an unsaved choice
  // from the last number onto this one.
  const current = idOf(device);
  useEffect(() => {
    setIdText(current == null ? '' : String(current));
    setNote(null);
  }, [device?.UniqueNumber, current]);

  const typed = Number(String(idText).trim());
  const wanted = Number.isFinite(typed) && typed > 0 ? typed : null;

  // The two inputs are one value: picking a name fills the number, typing a
  // number selects the name — the dropdown SHOWS what the number says.
  // ⚠️ NO SECOND COPY OF IT (bug, 2026-10-18). A `choice` state used to mirror
  // this; after Save the row re-read reset it to null, and with the number
  // already the new id nothing re-filled it — "Device type" sat blank beside
  // "ID 25" on the Message Box bench.
  const selected = useMemo(
    () => types.find((t) => t.deviceId === wanted) || null,
    [types, wanted],
  );

  const binOf = (id) => {
    const row = rows.find((r) => Number(r.DeviceID) === Number(id));
    return String(row?.BinFileName || '').trim();
  };
  const changing = wanted != null && wanted !== current;
  const fromBin = binOf(current);
  const toBin = binOf(wanted);
  // The device's OWN bin file wins over its type's, so a device that carries
  // one is not affected by the type's at all.
  const ownBin = String(device?.OTABinFileId || '').trim() !== ''
    && Number(device?.OTABinFileId) > 0;

  const save = async () => {
    if (!changing || !device?.UniqueNumber) return;
    setBusy(true);
    setNote(null);
    try {
      await setDeviceType(device.UniqueNumber, wanted);
      setNote({
        severity: 'success',
        text: `${device.UniqueNumber}: DeviceID ${current} → ${wanted}`,
      });
      if (onSaved) onSaved();
    } catch (e) {
      setNote({ severity: 'error', text: 'Could not change the type. Nothing was saved.' });
    } finally {
      setBusy(false);
    }
  };

  if (!device) return null;

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{
        display: 'flex', gap: 0.75, alignItems: 'center',
        flexWrap: dense ? 'wrap' : 'nowrap',
      }}
      >
        <Autocomplete
          size="small"
          sx={{ flex: dense ? '1 1 100%' : '1 1 auto', minWidth: 160 }}
          options={types}
          value={selected}
          onChange={(_, v) => setIdText(v ? String(v.deviceId) : '')}
          getOptionLabel={(t) => t.label || ''}
          isOptionEqualToValue={(o, v) => o.deviceId === v.deviceId}
          // The id is in the label, so a number typed here finds the type too.
          renderOption={(props, option) => (
            <li {...props} key={option.deviceId}>{option.label}</li>
          )}
          renderInput={(p) => <TextField {...p} label="Device type" />}
        />
        <TextField
          size="small"
          label="ID"
          value={idText}
          onChange={(e) => setIdText(e.target.value.replace(/[^\d]/g, ''))}
          sx={{ width: 78, flexShrink: 0 }}
          inputProps={{ inputMode: 'numeric', style: { fontSize: 13 } }}
        />
        <Button
          size="small"
          variant="contained"
          disabled={!changing || busy}
          onClick={save}
          sx={{ flexShrink: 0 }}
        >
          {busy ? '…' : 'Save'}
        </Button>
      </Box>

      {/* ⚠️ Said before the write, not after: which image the device would be
          served is the expensive half of a type change. */}
      {changing && !ownBin && fromBin !== toBin && (
        <Alert severity="warning" sx={{ mt: 0.5, py: 0, px: 1 }}>
          <Typography variant="caption">
            {`Firmware served changes: ${fromBin || '(none)'} → ${toBin || '(none)'}`}
          </Typography>
        </Alert>
      )}
      {changing && ownBin && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          This device has its own bin file, so the type&apos;s firmware does not apply to it.
        </Typography>
      )}
      {wanted != null && !selected && changing && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {`DeviceID ${wanted} is not in the catalogue — it will still be saved.`}
        </Typography>
      )}
      {note && (
        <Alert severity={note.severity} sx={{ mt: 0.5, py: 0, px: 1 }} onClose={() => setNote(null)}>
          <Typography variant="caption">{note.text}</Typography>
        </Alert>
      )}
    </Box>
  );
}
